import { createContext } from 'react'

/**
 * The ability to edit/update goal properties might take place deep inside a goal
 * component’s hierarchy, so we define this context to expose functions that will
 * correctly update state affected by such edits.
 */
const GoalUpdateContext = createContext({
  updatedAt: undefined, // Some use cases need this to determine if a goal has changed.

  draft: false,
  checklistUpdated: (activeItems, checkedItems) => {},
  notesUpdated: notes => {},
  relatedDocumentsAttached: attachedPosts => {},
  relatedDocumentDetached: detachedPost => {},
  relatedDocumentsUpdated: relatedDocs => {},
  statusUpdated: status => {}
})

/**
 * Maintains a range of values and functions pertaining to a particular category
 * of goals, with the overall list held in GOAL_CATEGORIES in the goalHelpers
 * module. At any given moment, it holds a current goal category. Given that
 * category, it manages the current list of goals belonging to that category
 * and handles changes to that list. A change to the goal category resets the
 * context, now switching to goals in that category.
 *
 * By design, the context contains values and functions that correspond to the
 * useCrudCache and useLoadMore hooks. This provides consistency with how other
 * components interact with their corresponding model objects.
 */
const GoalCategoryContext = createContext({
  goals: [], // Works like useCrudCache.items
  goalCreated: createdGoal => {}, // Works like useCrudCache.itemCreated
  goalUpdated: updatedGoal => {}, // Works like useCrudCache.itemUpdated
  goalDeleted: deletedGoal => {}, // Works like useCrudCache.itemDeleted
  deletedGoal: null, // Works like useCrudCache.deletedItem
  goalsComplete: true, // Works like useLoadMore.itemsComplete
  goalsPaging: false, // Works like useLoadMore.itemsPaging
  firstGoalsPage: () => {}, // Truncates the goals list to the first page.
  nextGoalsPage: () => {}, // Works like useLoadMore.nextItemsPage
  allGoalsPages: () => {}, // Works like useLoadMore.allItemsPages

  household: null, // The household for which goals are being loaded
  setGoalCategory: goalCategory => {} // Unique to GoalCategoryContext
})

/**
 * Provides access (if available) to functionality for creating or editing
 * goals. Typically available in non-default form only to admin components.
 */
const GoalEditorContext = createContext({
  createGoal: null, // But when available, should be () => {}
  editGoal: null // But when available, should be goal => {}
})

const SuggestedGoalButtonContext = createContext({
  onSave: null, // its own functionality to save a goal
  saveLabel: null, // funtion used to generate label
  saveInProgress: () => false, // function helps to maintains inprogress state with boolean value
  saveButtonVariant: null, // function helps to change visual styles of button
  hideSaveButton: null // intially null to pass ‘??’ check, then returns boolean to toggle save button visibility
})

/**
 * Defaults in this context are applicable to the parent portal.
 *
 * Other environments (e.g., services portal) should provide their
 * alternative values, if needed.
 */
const PlanNavigationContext = createContext({
  // The function that returns the path to the given goal ID.
  goalPath: (goalId, clientId) => `/roadmaps/${clientId}/goals/${goalId}`,
  goalEditPath: (goalId, clientId) => `/roadmaps/${clientId}/goals/${goalId}/edit`,

  // Whether to label the goal overview with the goal’s category. If true, that will
  // be the text for the goal overview link and `goalOverviewLabel` will be ignored.
  // If false, `goalOverviewLabel` will be used instead.
  overviewLabelIsCategory: false,

  // The link label for the goal overview, if useCategoryForOverviewLabel is false.
  goalOverviewLabel: 'Roadmap',

  // The path to the goal overview.
  goalOverviewPath: clientId => `/roadmaps/${clientId}`,

  // Path to direct audit access (only available to audits with an alias).
  auditAliasPath: (auditAlias, childId) => `/audits/${auditAlias}${childId ? `/${childId}` : ''}`,

  // Paths relating to IEPs.
  iepPath: (iepId, clientId) => `/edu/${clientId}/${iepId}`,
  iepUploadPath: clientId => `/edu/${clientId}`,

  // The path to the plan checklist.
  checklistPath: '/roadmaps/checklist',

  // The path to user events.
  eventsPath: '/events'
})

/**
 * Information about “who’s in a goal” is controlled by the chat service because
 * it is determined by membership in a goal’s chat channel. This is determined
 * after the goals are loaded so we need a different mechanism for reporting
 * when a user isn’t allowed to access a goal. Components that need to change
 * how they display goals based on membership should provide this context.
 */
const GoalValidityContext = createContext({
  // This function should be called if a goal isn’t considered valid for the current
  // situation (user, household, etc.—whatever the caller deems appropriate).
  reportForbiddenGoal: forbiddenGoalId => {}
})

export {
  GoalUpdateContext,
  GoalCategoryContext,
  GoalEditorContext,
  PlanNavigationContext,
  GoalValidityContext,
  SuggestedGoalButtonContext
}
